import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS } from '@bifeai/shared';
import { Avatar, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@bifeai/ui';
import { BadgeCheck, Building2, ChevronDown, CircleHelp, LogOut, Map, Rocket, Settings, UserRound, Users } from 'lucide-react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { clearSnapshots } from '../lib/offline';
import { setHelp, startTour, useHelp } from '../lib/help';
import { useSession } from '../lib/session';

/**
 * Menú de la cuenta (arriba a la derecha): configuración, sucursales, usuarios,
 * mi cuenta, ayudas (puesta en marcha, recorrido guiado) y cerrar sesión.
 */
export function UserMenu() {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const help = useHelp(me.user.id);
  const owner = can('settings.manage') || can('users.manage');

  const logout = async () => {
    await api.post('/auth/logout').catch(() => undefined);
    clearSnapshots();
    qc.clear();
    navigate('/ingresar', { replace: true });
  };

  return (
    <Menu>
      <MenuTrigger asChild>
        <button type="button" className="bf-user-btn" aria-label={`Cuenta de ${me.user.name}`} data-tour="user-menu">
          <Avatar name={me.user.name} />
          <ChevronDown />
        </button>
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>
          <strong style={{ display: 'block', color: 'var(--bf-ink)', fontSize: 'var(--bf-text-md)' }}>{me.user.name}</strong>
          {ROLE_LABELS[me.membership.role]} en {me.tenant.name}
        </MenuLabel>
        <MenuSeparator />
        {can('settings.manage') ? (
          <MenuItem icon={<Settings />} onSelect={() => navigate('/configuracion')}>
            Configuración
          </MenuItem>
        ) : (
          can('franchise.accounts') && (
            <MenuItem icon={<Settings />} onSelect={() => navigate('/configuracion')}>
              Cuentas de la franquicia
            </MenuItem>
          )
        )}
        <MenuItem icon={<Building2 />} onSelect={() => navigate('/sucursales')}>
          Sucursales
        </MenuItem>
        {can('users.manage') && (
          <MenuItem icon={<Users />} onSelect={() => navigate('/usuarios')}>
            Usuarios
          </MenuItem>
        )}
        {(can('settings.manage') || me.membership.role === 'FRANCHISEE') && (
          <MenuItem icon={<BadgeCheck />} onSelect={() => navigate('/plan')}>
            Mi plan
          </MenuItem>
        )}
        <MenuItem icon={<UserRound />} onSelect={() => navigate('/cuenta')}>
          Mi cuenta
        </MenuItem>
        <MenuSeparator />
        {owner && (
          <MenuItem
            icon={<Rocket />}
            checked={help.setup !== 'hidden'}
            onSelect={() => setHelp(me.user.id, { setup: help.setup === 'hidden' ? 'open' : 'hidden' })}
          >
            Puesta en marcha
          </MenuItem>
        )}
        <MenuItem icon={<Map />} onSelect={() => startTour('general')}>
          Recorrido guiado
        </MenuItem>
        <MenuItem icon={<CircleHelp />} checked={help.tips} onSelect={() => setHelp(me.user.id, { tips: !help.tips })}>
          Mostrar ayudas (?)
        </MenuItem>
        <MenuSeparator />
        <MenuItem icon={<LogOut />} danger onSelect={() => void logout()}>
          Cerrar sesión
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
